import { supabase } from './supabase.helpers'

/**
 * Who proposed the pending drafts of an experience (05.4-T1).
 *
 * The names come from an edge function because the client CANNOT resolve them:
 * `profiles` is readable only by its own user, so a nested join from the
 * dashboard query returns null for every proposer by construction (verified
 * during 05.6).
 *
 * A 403 is not an error here: the function is gated on the `moderate`
 * capability, so the failure IS the answer — this caller gets no moderation
 * controls. That is why the result carries `canModerate` instead of throwing.
 */
export const getProposalContext = async ({
  experienceId,
}: {
  experienceId: string
}): Promise<{ canModerate: boolean; proposers: Record<string, string> }> => {
  const { data, error } = await supabase.functions.invoke('get-proposal-context', {
    body: { experienceId },
  })

  if (error) {
    return { canModerate: false, proposers: {} }
  }

  return { canModerate: true, proposers: data?.proposers ?? {} }
}

/**
 * Rejects every spot of a proposal group, one RPC call each (05.4-T8): the
 * grouping brings the bulk action with it, and it needs nothing more than
 * iterating `reject_agami_draft` — each call is its own transaction.
 *
 * Partial failure is possible by design (seven rejected out of twelve, then an
 * error) and is NOT hidden: the counts come back so the caller can say it
 * honestly. The spots that failed are still pending — retrying is safe.
 */
export const rejectProposalGroup = async (spotIds: string[]): Promise<{ total: number; failed: number }> => {
  const results = await Promise.allSettled(
    spotIds.map(async (spotId) => {
      const { error } = await supabase.rpc('reject_agami_draft', { p_spot_id: spotId })
      if (error) throw error
    })
  )

  return {
    total: spotIds.length,
    failed: results.filter((r) => r.status === 'rejected').length,
  }
}
